import { type CalendarDate, type Time, parseDate, parseTime } from "@internationalized/date";
import { DateTime } from "luxon";
import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { WizardParkingChoice } from "../../../../PermitContextProvider/types";
import { makePermitIntervals } from "../../../../makePermitIntervals";

type IntervalFormData = {
	intervals: {
		startDate: CalendarDate;
		endDate: CalendarDate;
		startTime: Time;
		endTime: Time;
		durationInMinutes?: number;
	}[];
};

export const useEndTimeRecalculation = (
	wizardSettings: WizardParkingChoice["time"],
	timezone: string,
	methods: UseFormReturn<IntervalFormData>,
) => {
	const startDate = methods.watch("intervals.0.startDate");
	const startTime = methods.watch("intervals.0.startTime");

	useEffect(() => {
		if (startDate && startTime) {
			const currentInterval = {
				startDate: startDate.toString(),
				startTime: startTime.toString(),
			};
			const computed = makePermitIntervals(wizardSettings, timezone, [currentInterval]);
			if (computed[0]?.endDate) {
				methods.setValue("intervals.0.endDate", parseDate(computed[0].endDate));
			}
			if (computed[0]?.endTime) {
				methods.setValue("intervals.0.endTime", parseTime(computed[0].endTime));
			}
		}
	}, [startDate, startTime, wizardSettings, timezone, methods]);

	const recalculateEndTimeFromDuration = (index: number, startDate: CalendarDate, startTime: Time) => {
		const durationInMinutes = methods.watch(`intervals.${index}.durationInMinutes`);
		if (durationInMinutes) {
			const startDateTime = DateTime.fromFormat(
				`${startDate.toString()} ${startTime.toString()}`,
				"yyyy-MM-dd HH:mm:ss",
				{ zone: timezone },
			);
			const endDateTime = startDateTime.plus({ minutes: durationInMinutes });
			methods.setValue(`intervals.${index}.endDate`, parseDate(endDateTime.toISODate()!), {
				shouldValidate: true,
			});
			methods.setValue(`intervals.${index}.endTime`, parseTime(endDateTime.toFormat("HH:mm:ss")), {
				shouldValidate: true,
			});
		}
	};

	return { recalculateEndTimeFromDuration };
};
